import { css, styled } from '@vebox/etui-system';
import '../utilities/format-ms.js';
import '../utilities/create-transition.js';
import { useState, useRef, createElement } from 'react';
import '../utilities/get-styleprop.js';
import { a as _objectSpread2, _ as _objectWithoutProperties, b as _extends } from '../_rollupPluginBabelHelpers-3b7bd988.js';
import { Div } from '@vebox/etui-system-elements';
import '../Svg-0180d7b5.js';
import '../Icon/Icon.js';
import '@vebox/etui-icons';
import '../Icon/icons.js';
import '../Icon/helper.js';
import { fade, darken } from '@vebox/etui-colors';
import '../ClickOutside/ClickOutside.js';
import '../Tooltip/Tooltip.js';
import '../Button/Button.js';
import { DayPicker } from 'react-day-picker';
import { Navbar } from './NavBar.js';
import 'date-fns';
import { formatWeekdayName, formatCaption } from './formatters.js';

const buttonReset = css`
  .rdp-button {
    background-color: transparent;
    border-width: 0;
    font-family: inherit;
    font-size: inherit;
    font-style: inherit;
    font-weight: inherit;
    line-height: inherit;
    padding: 0;
  }
`;
const DatePickerWrapper = styled(Div)`
  position: relative;
  ${buttonReset}
  ${({
  theme
}) => css`
    .rdp {
      font-family: ${theme.typography.fontFamily};
      font-size: ${theme.typography.fontSizeBase};
      box-shadow: ${theme.shadows[4]};
      &-table {
        border-collapse: collapse;
      }
      &-multiple_months > .rdp-months {
        display: flex;
        .rdp-month:not(:last-child) {
          margin-right: ${theme.spacing.sm}px;
        }
      }
      &-navbar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        background-color: ${theme.palette.grey[10]};
        color: ${theme.palette.text.secondary};
        font-weight: ${theme.typography.fontWeightBold};
      }

      &-head {
        background-color: ${theme.palette.grey[10]};
      }

      &-head_cell {
        padding: 0.5em;
        color: ${theme.palette.text.secondary};
        text-align: center;
        font-size: ${theme.typography.pxToRem(13)};
        text-transform: capitalize;
        border: none;
        font-weight: ${theme.typography.fontWeightRegular};
      }

      &-cell {
        border: 1px solid ${theme.palette.divider};
        padding: 0;
        background-color: ${theme.palette.common.white};
      }

      &-day {
        width: 100%;
        height: 100%;
        vertical-align: middle;
        text-align: center;
        cursor: pointer;
        white-space: pre;
        border-radius: 0;
        padding: ${theme.spacing.xxs}px ${theme.spacing.xs}px;
        outline: none;

        &_outside,
        &_disabled {
          color: #dce0e0;
          cursor: default;
        }

        &_selected {
          background-color: ${theme.palette.primary.main};
          color: ${theme.palette.common.white};
        }

        &_range_middle {
          background-color: ${fade(theme.palette.primary.main, 0.5)};
          color: ${darken(theme.palette.primary.main, 0.2)};
        }
      }
    }
  `}
`;

const MonthPicker = styled(Div).attrs(props => ({
  display: 'flex',
  flexWrap: 'wrap',
  bg: props.theme.palette.common.white,
  height: '100%'
}))``;

const MonthPickerButton = styled(Div).attrs(props => _objectSpread2({
  p: `${props.theme.spacing.md}px`,
  borderTop: `1px solid ${props.theme.palette.divider}`,
  borderRight: `1px solid ${props.theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
  overflow: 'hidden'
}, props))`
  width: 25%;
  background: none;
  cursor: pointer;

  &:hover {
    background-color: #e0e0e0;
  }

  &:focus {
    outline: none;
    border-color: #007bff;
  }

  &:nth-child(4n) {
    border-right: none; /* Remove right border for every 4th item */
  }

  &:nth-last-child(-n + 4) {
    border-bottom: none; /* Remove bottom border for the last row */
  }
`;

const MonthPickerRoot = styled(Div).attrs(props => _objectSpread2({}, props))`
  position: absolute;
  top: 40px;
  left: 0;
  width: 100%;
  height: calc(100% - 40px);
  background-color: rgba(255, 255, 255, 0.9);
  z-index: 1;
`;

const _excluded = ["mode", "useMonthSelector", "components", "formatters"];
const DatePicker = _ref => {
  let {
      mode = 'single',
      useMonthSelector = false,
      components,
      formatters
    } = _ref,
    rest = _objectWithoutProperties(_ref, _excluded);
  const [isMonthPickerVisible, setIsMonthPickerVisible] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [headerNavigationYear, setHeaderNavigationYear] = useState(new Date().getFullYear());
  const navbarRef = useRef(null);
  const handleMonthChange = month => {
    var _navbarRef$current;
    setCurrentMonth(month);
    setIsMonthPickerVisible(false);
    const newMonth = new Date(headerNavigationYear, month.getMonth());
    (_navbarRef$current = navbarRef.current) === null || _navbarRef$current === void 0 ? void 0 : _navbarRef$current.goToMonth(newMonth);
  };
  const handleNavigationMonthChange = month => {
    setHeaderNavigationYear(month.getFullYear());
  };
  const handleMonthClick = () => {
    setIsMonthPickerVisible(prev => !prev);
  };
  const renderMonthPicker = () => {
    const months = Array.from({
      length: 12
    }, (_, i) => new Date(currentMonth.getFullYear(), i));
    return /*#__PURE__*/createElement(MonthPicker, null, months.map((month, index) => /*#__PURE__*/createElement(MonthPickerButton, {
      key: index,
      onClick: () => handleMonthChange(month)
    }, month.toLocaleString('default', {
      month: 'short'
    }))));
  };
  return /*#__PURE__*/createElement(DatePickerWrapper, null, /*#__PURE__*/createElement(DayPicker, _extends({
    mode: mode,
    components: _objectSpread2({
      Caption: props => /*#__PURE__*/createElement(Navbar, _extends({}, props, {
        onMonthClick: handleMonthClick,
        onNavigationMonthChange: handleNavigationMonthChange,
        navigationYear: headerNavigationYear,
        useMonthSelector: useMonthSelector,
        isMonthSelectorVisible: isMonthPickerVisible,
        ref: navbarRef
      }))
    }, components),
    formatters: _objectSpread2({
      formatWeekdayName,
      formatCaption
    }, formatters)
  }, rest)), isMonthPickerVisible && /*#__PURE__*/createElement(MonthPickerRoot, null, renderMonthPicker()));
};

export default DatePicker;
